/**
 * New Collection Components
 * A form-based interface for creating new NFT collections with comprehensive metadata
 * Includes features for managing collection details, ERC721 metadata, authentication, and production phases
 */

import React, { useState } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import { addNotification } from "../../store/notification-store";
import { v4 as uuidv4 } from "uuid";
import { setConfig } from "../../store/config/config-slice";
import LoadingSpinner from "../../utils/loading-spinner";
import ConfirmationDialog from "../../utils/confirmation-dialog";

/**
 * NewCollection Component
 * A form component for creating and configuring new NFT collections
 *
 * Features:
 * - Collection metadata management (name, ticker, item count)
 * - Image upload and preview
 * - ERC721 standard metadata configuration
 * - Custom attributes management
 * - Authentication details
 * - Production phase tracking
 *
 * The component handles form state, image previews, and submission to the blockchain
 * through a series of controlled inputs and state management hooks.
 *
 * @returns {JSX.Element} A form interface for creating new NFT collections
 */
const NewCollection = () => {
  const dispatch = useDispatch();
  const [itemNumber, setItemNumber] = useState();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    image: "",
    external_url: "",
    attributes: [],
    authentication: {
      uniqueId: "",
      serialNumber: "",
      manufactureDate: "",
      isAdditionalMint: false,
    },
    productionPhases: {},
  });
  const [collectionImage, setCollectionImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  const [newPhase, setNewPhase] = useState("");
  const [newAttribute, setNewAttribute] = useState({
    trait_type: "",
    value: "",
  });
  const [newParameter, setNewParameter] = useState({
    name: "",
    value: "",
    phase: "",
  });
  const [showParameterForm, setShowParameterForm] = useState(false);
  const [collectionName, setCollectionName] = useState("");
  const [collectionTicker, setCollectionTicker] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  /**
   * Handles file upload for collection images
   * Processes the uploaded file and creates a preview URL
   * @param {Event} e - The file input change event
   */
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
        setCollectionImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  /**
   * Updates the form data state for standard input fields
   * @param {Event} e - The input change event
   */
  const handleStandardFieldChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  /**
   * Processes collection item count changes
   * @param {Event} e - The input change event
   */
  const handleCollectionItemChange = (e) => {
    setItemNumber(parseInt(e.target.value));
  };

  /**
   * Updates authentication-related form fields
   * @param {Event} e - The input change event
   */
  const handleAuthenticationChange = (e) => {
    setFormData({
      ...formData,
      authentication: {
        ...formData.authentication,
        [e.target.name]: e.target.value,
      },
    });
  };

  /**
   * Manages collection name updates
   * @param {Event} e - The input change event
   */
  const handleCollectionNameChange = (e) => {
    setCollectionName(e.target.value);
  };

  /**
   * Handles updates to the collection ticker symbol
   * @param {Event} e - The input change event
   */
  const handleCollectionTickerChange = (e) => {
    setCollectionTicker(e.target.value);
  };

  /**
   * Adds a new attribute to the collection metadata
   * Validates and appends attribute to the existing list
   */
  const addAttribute = () => {
    if (newAttribute.trait_type && newAttribute.value) {
      setFormData({
        ...formData,
        attributes: [...formData.attributes, { ...newAttribute }],
      });
      setNewAttribute({ trait_type: "", value: "" });
    }
  };

  /**
   * Creates a new production phase in the collection lifecycle
   * Adds an empty phase object to the production phases collection
   */
  const addProductionPhase = () => {
    if (newPhase) {
      setFormData({
        ...formData,
        productionPhases: {
          ...formData.productionPhases,
          [newPhase]: {},
        },
      });
      setNewPhase("");
    }
  };

  /**
   * Initiates the parameter addition process for a specific phase
   * @param {string} phase - The production phase to add parameters to
   */
  const addPhaseParameter = (phase) => {
    setNewParameter({ ...newParameter, phase });
    setShowParameterForm(true);
  };

  /**
   * Processes and saves new parameter data to a production phase
   * Validates parameter data before adding to the phase configuration
   */
  const handleParameterSubmit = () => {
    if (newParameter.name && newParameter.value) {
      setFormData({
        ...formData,
        productionPhases: {
          ...formData.productionPhases,
          [newParameter.phase]: {
            ...formData.productionPhases[newParameter.phase],
            [newParameter.name]: newParameter.value,
          },
        },
      });
      setNewParameter({ name: "", value: "", phase: "" });
      setShowParameterForm(false);
    }
  };

  /**
   * Handles form submission and blockchain transaction initiation
   * Processes form data and initiates the collection creation transaction
   * @param {Event} e - The form submission event
   */
  const handleSubmit = async () => {
    // e.preventDefault();
    setIsLoading(true);
    try {
      const request = {
        collectionName: collectionName,
        collectionTicker: collectionTicker,
        itemCollectionNumber: itemNumber,
        ercData: formData,
        collectionImage: collectionImage,
      };

      const result = await axios.post(
        process.env.REACT_APP_SERVER_URL + "/api/new-collection",
        request,
      );
      const newNotification = {
        id: uuidv4(),
        message: result.data.message,
        type: "success",
      };
      dispatch(addNotification(newNotification));
      // update customerConfig
      const customerConfig = await axios.get(
        process.env.REACT_APP_SERVER_URL + "/api/customer-configurations",
      );

      dispatch(setConfig(customerConfig.data));
    } catch {
      const newNotification = {
        id: uuidv4(),
        message: "Error during the process",
        type: "error",
      };
      dispatch(addNotification(newNotification));
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = () => {
    handleSubmit();
  };

  const handleCancel = () => null;

  return (
    <>
      <LoadingSpinner
        isLoading={isLoading}
        text="Caricamento collezione in corso, attendere."
      />
      <div className="mx-auto max-w-4xl p-6 m-8">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setIsDialogOpen(true);
          }}
          className="space-y-8 w-full max-w-4xl"
          disabled={isLoading}
        >
          <h1 className="text-2xl font-bold mb-8">Crea collezione</h1>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <label
                  htmlFor="itemNumber"
                  className="block text-sm font-medium text-gray-700"
                >
                  Nome collezione
                </label>
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  id="collectionName"
                  type="text"
                  name="collectionName"
                  onChange={handleCollectionNameChange}
                />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="ticker"
                  className="block text-sm font-medium text-gray-700"
                >
                  Ticker
                </label>
                <input
                  id="collectionTicker"
                  name="collectionTicker"
                  type="text"
                  value={formData.ticker}
                  onChange={handleCollectionTickerChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="itemNumber"
                  className="block text-sm font-medium text-gray-700"
                >
                  Numero asset collezione
                </label>
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  id="itemNumber"
                  type="number"
                  name="itemNumber"
                  onChange={handleCollectionItemChange}
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Collection Image
                </label>
                <div className="flex flex-col gap-4">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  {previewUrl && (
                    <div className="relative w-48 h-48">
                      <img
                        src={previewUrl}
                        alt="Collection preview"
                        className="w-full h-full object-cover rounded-md"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="mb-4">
                <h2 className="text-xl font-bold">ERC721 Standard Metadata</h2>
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleStandardFieldChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700"
                >
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleStandardFieldChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="image"
                  className="block text-sm font-medium text-gray-700"
                >
                  Image URL
                </label>
                <input
                  id="image"
                  name="image"
                  type="text"
                  value={formData.image}
                  onChange={handleStandardFieldChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="external_url"
                  className="block text-sm font-medium text-gray-700"
                >
                  External URL
                </label>
                <input
                  id="external_url"
                  name="external_url"
                  type="text"
                  value={formData.external_url}
                  onChange={handleStandardFieldChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Attributes
                </label>
                <div className="flex gap-2">
                  <input
                    placeholder="Trait Type"
                    value={newAttribute.trait_type}
                    onChange={(e) =>
                      setNewAttribute({
                        ...newAttribute,
                        trait_type: e.target.value,
                      })
                    }
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    placeholder="Value"
                    value={newAttribute.value}
                    onChange={(e) =>
                      setNewAttribute({
                        ...newAttribute,
                        value: e.target.value,
                      })
                    }
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={addAttribute}
                    className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  >
                    +
                  </button>
                </div>
                <div className="mt-2">
                  {formData.attributes.map((attr, index) => (
                    <div key={index} className="flex gap-2 items-center mt-2">
                      <span>
                        {attr.trait_type}: {attr.value}
                      </span>
                      <button
                        type="button"
                        className="p-1 text-red-500 hover:bg-red-100 rounded"
                        onClick={() => {
                          const newAttributes = [...formData.attributes];
                          newAttributes.splice(index, 1);
                          setFormData({
                            ...formData,
                            attributes: newAttributes,
                          });
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="mb-4">
              <h2 className="text-xl font-bold">Authentication</h2>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <label
                  htmlFor="uniqueId"
                  className="block text-sm font-medium text-gray-700"
                >
                  Unique ID
                </label>
                <input
                  id="uniqueId"
                  name="uniqueId"
                  type="text"
                  value={formData.authentication.uniqueId}
                  onChange={handleAuthenticationChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="serialNumber"
                  className="block text-sm font-medium text-gray-700"
                >
                  Serial Number
                </label>
                <input
                  id="serialNumber"
                  name="serialNumber"
                  type="text"
                  value={formData.authentication.serialNumber}
                  onChange={handleAuthenticationChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="manufactureDate"
                  className="block text-sm font-medium text-gray-700"
                >
                  Manufacture Date
                </label>
                <input
                  id="manufactureDate"
                  name="manufactureDate"
                  type="date"
                  value={formData.authentication.manufactureDate}
                  onChange={handleAuthenticationChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="isAdditionalMint" className="flex items-center">
                  <input
                    id="isAdditionalMint"
                    name="isAdditionalMint"
                    type="checkbox"
                    checked={formData.authentication.isAdditionalMint}
                    onChange={(e) =>
                      handleAuthenticationChange({
                        target: {
                          name: "isAdditionalMint",
                          value: e.target.checked,
                        },
                      })
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    disabled
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Is Additional Mint
                  </span>
                </label>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="mb-4">
              <h2 className="text-xl font-bold">Production Phases</h2>
            </div>
            <div className="space-y-4">
              <div className="flex gap-2">
                <input
                  placeholder="New Phase Name"
                  value={newPhase}
                  onChange={(e) => setNewPhase(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={addProductionPhase}
                  className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  +
                </button>
              </div>

              {Object.keys(formData.productionPhases).map((phase) => (
                <div key={phase} className="mt-4 bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-4">{phase}</h3>
                  <button
                    type="button"
                    onClick={() => addPhaseParameter(phase)}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100"
                  >
                    Add Parameter
                  </button>

                  {Object.entries(formData.productionPhases[phase]).map(
                    ([param, value]) => (
                      <div key={param} className="mt-2 flex gap-2 items-center">
                        <span>
                          {param}: {value}
                        </span>
                        <button
                          type="button"
                          className="p-1 text-red-500 hover:bg-red-100 rounded"
                          onClick={() => {
                            const newPhases = { ...formData.productionPhases };
                            delete newPhases[phase][param];
                            setFormData({
                              ...formData,
                              productionPhases: newPhases,
                            });
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ),
                  )}
                </div>
              ))}
            </div>
          </div>
          {showParameterForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <div className="bg-white p-6 rounded-lg shadow-lg w-96">
                <h3 className="text-lg font-semibold mb-4">Add Parameter</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Parameter Name
                    </label>
                    <input
                      type="text"
                      value={newParameter.name}
                      onChange={(e) =>
                        setNewParameter({
                          ...newParameter,
                          name: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Parameter Value
                    </label>
                    <input
                      type="text"
                      value={newParameter.value}
                      onChange={(e) =>
                        setNewParameter({
                          ...newParameter,
                          value: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleParameterSubmit}
                      className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowParameterForm(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          <button
            type="submit"
            className="w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            disabled={isLoading}
          >
            Start Blockchain transaction
          </button>
        </form>
      </div>
      <ConfirmationDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title="Conferma azione"
        content="Sei sicuro di voler procedere con questa azione? Andando avanti verranno inseriti dei dati in Blockchain maniera irreversibile"
        showConfirm={true}
        showCancel={true}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
};

export default NewCollection;
